@value tablet from "@shared/styles/breakpoints.module.css";

.pageRoot {
  width: 100%;
  max-width: var(--layout-content-max);
  margin: 0 auto;
}

.pageHeader {
  padding: clamp(var(--space-32), 4vw, var(--space-64)) var(--space-page)
    clamp(var(--space-20), 2.5vw, var(--space-40));
  border-bottom: 1px solid var(--color-border);
  background: var(--color-bg);
}

.badge {
  font-size: var(--type-caption);
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--color-text-muted);

  margin-bottom: var(--space-10);
}

.pageTitle {
  font-family: var(--font-heading);
  font-size: var(--type-page);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: var(--color-text);
  line-height: 1.05;
}

.overview {
  padding: var(--space-24) var(--space-page);
  border-bottom: 1px solid var(--color-border);
  background: var(--color-bg);
  display: flex;
  justify-content: center;
}

.overviewButton {
  position: relative;
  display: block;
  width: min(100%, 960px);
  padding: 0;
  overflow: hidden;
  border-radius: var(--radius-medium);
  border: none;
  background: none;
  cursor: zoom-in;
}

.overviewImg {
  display: block;
  width: 100%;
  height: auto;
}

.overviewHint {
  position: absolute;
  right: var(--space-10);
  bottom: var(--space-10);
  display: flex;
  padding: var(--space-6);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-small);
  background: var(--color-card);
  color: var(--color-text);
  opacity: 0;
  transition: opacity var(--motion-fast) ease;

  svg {
    width: 16px;
    height: 16px;
  }

  .overviewButton:hover &,
  .overviewButton:focus-visible & {
    opacity: 1;
  }

  @media (hover: none) {
    opacity: 1;
  }
}

.emptyState {
  font-size: var(--type-caption);
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--color-text-muted);

  padding: clamp(var(--space-48), 6vw, var(--space-96)) var(--space-page);
  font-family: var(--font-heading);
  font-size: clamp(0.875rem, 1vw, 0.9375rem);
}

.backButton {
  font-size: var(--type-caption);
  font-weight: 700;
  letter-spacing: normal;
  text-transform: none;
  color: var(--color-text-muted);

  text-decoration: none;
  display: flex;
  align-items: center;
  gap: var(--space-8);
  width: fit-content;
  min-height: var(--layout-control-height);
  padding: var(--space-12) 0;
  background: transparent;
  font-family: var(--font-body);
  font-size: var(--type-caption);
  transition: color var(--motion-fast) ease;

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: var(--color-primary);
    }
  }
}

.categoryNav {
  padding: var(--space-20) clamp(var(--space-24), 4vw, var(--space-64)) 0;
}

.categoryLink {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: var(--space-4);
  width: fit-content;
  min-height: var(--layout-control-height);
  color: var(--color-text);
  text-decoration: none;
  transition: color var(--motion-fast) ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: var(--color-primary);
    }
  }
}

.categoryTitle {
  font-family: var(--font-heading);
  font-size: var(--type-catalog);
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: var(--space-4);
  text-wrap: pretty;
}

.categoryHint {
  color: var(--color-text-muted);
  font-size: var(--type-caption);
}

.detailLayout {
  display: grid;
  grid-template-columns: minmax(0, 1fr);

  @media (max-width: tablet) {
    grid-template-columns: minmax(0, 1fr);
  }
}

.productPanel {
  padding: clamp(var(--space-24), 3vw, var(--space-48)) clamp(var(--space-24), 4vw, var(--space-64));

  background: transparent;
}

.media {
  display: block;
}

.info {
  display: block;
}

.info[data-overview="true"] {
  margin-top: var(--space-32);
}

.overviewSummary {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  align-items: start;
  gap: var(--space-24) clamp(var(--space-24), 4vw, var(--space-64));
}

.overviewSummary .detailDesc {
  grid-column: 1;
  max-width: 70ch;
  margin: 0;
}

.overviewSummary .specsTable {
  grid-column: 1;
  max-width: 70ch;
}

.overviewSummary .specLabel {
  width: 45%;
  hyphens: none;
}

.overviewSummary .ctaLink {
  grid-column: 2;
  grid-row: 1;
  margin-top: 0;
}

@media (max-width: tablet) {
  .overviewSummary {
    display: block;
  }
  .overviewSummary .specsTable,
  .overviewSummary .ctaLink {
    margin-top: var(--space-24);
  }
}

.guidePanel {
  padding: clamp(var(--space-24), 3vw, var(--space-48)) clamp(var(--space-24), 4vw, var(--space-64));

  background: var(--color-bg);
}

.mainPhoto {
  font-size: var(--type-caption);
  font-weight: 700;
  letter-spacing: normal;
  text-transform: none;
  color: var(--color-text-muted);

  width: 100%;
  padding: 0;
  cursor: default;
  position: relative;
  overflow: hidden;
  aspect-ratio: 4 / 3;
  max-height: 520px;
  border-radius: var(--radius-medium);
  border: none;
  background: var(--color-media-surface);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: clamp(var(--space-10), 1.5vw, var(--space-16));
}

.mainPhoto .mainPhotoImg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: clamp(var(--space-8), 1.5vw, var(--space-24));
}

.zoomHint {
  position: absolute;
  right: var(--space-10);
  bottom: var(--space-10);
  display: flex;
  padding: var(--space-6);
  border-radius: var(--radius-small);
  background: var(--color-card);
  color: var(--color-text);
  opacity: 0;
  transition: opacity var(--motion-fast) ease;

  svg {
    width: 16px;
    height: 16px;
  }
  .mainPhoto:hover &,
  .mainPhoto:focus-visible & {
    opacity: 1;
  }
  @media (hover: none) {
    opacity: 1;
  }
}

.thumbs {
  display: flex;
  gap: clamp(var(--space-6), 0.8vw, var(--space-10));
}

.thumb {
  /* не ниже 44px — удобная цель для пальца */
  width: clamp(63px, 6vw, 80px);
  height: clamp(44px, 4.5vw, 56px);
  padding: 0;
  border-radius: var(--radius-small);
  border: 1px solid var(--color-border);
  box-shadow: none;
  background: var(--color-media-surface);
  overflow: hidden;
  cursor: pointer;
  transition: border-color var(--motion-fast) ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      border-color: var(--color-primary);
    }
  }
}

.thumb .thumbImg {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: var(--space-3);
  display: block;
}

.specsTable {
  margin: 0;
  border: none;
  overflow: hidden;
}

.specRow {
  display: flex;
  border-bottom: 1px solid var(--color-border);

  &:last-child {
    border-bottom: none;
  }
}

.specLabel {
  font-size: var(--type-caption);
  font-weight: 700;
  letter-spacing: normal;
  text-transform: none;
  color: var(--color-text-muted);

  /* uppercase-подпись в узкой колонке: длинное слово переносится, а не вылезает за ячейку */
  overflow-wrap: anywhere;
  hyphens: auto;
  width: 38%;
  flex-shrink: 0;
  padding: clamp(var(--space-7), 0.9vw, var(--space-10))
    clamp(var(--space-10), 1.3vw, var(--space-16));
  background: transparent;
  border-right: none;
}

.specValue {
  margin: 0;
  flex: 1;
  min-width: 0;
  padding: clamp(var(--space-7), 0.9vw, var(--space-10))
    clamp(var(--space-10), 1.3vw, var(--space-16));
  font-size: clamp(0.875rem, 1vw, 0.9375rem);
  font-family: var(--font-heading);
  color: var(--color-text);
  overflow-wrap: anywhere;
}

.detailTitle {
  margin-bottom: clamp(var(--space-16), 2vw, var(--space-24));
  font-family: var(--font-heading);
  font-size: var(--type-item);
  font-weight: 600;
  text-transform: none;
  letter-spacing: normal;
  line-height: 1.1;

  min-width: 0;
  color: var(--color-text);
  text-wrap: pretty;
  overflow-wrap: anywhere;
}

.detailDesc {
  font-size: var(--type-body);
  line-height: 1.65;
  color: var(--color-text-muted);
  margin: clamp(var(--space-16), 2vw, var(--space-24)) 0
    clamp(var(--space-20), 2.5vw, var(--space-32));
}

.video {
  margin-top: clamp(var(--space-24), 3vw, var(--space-40));

  &:first-child {
    margin-top: 0;
  }
}

.videoLabel {
  font-size: var(--type-caption);
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--color-text-muted);

  margin-bottom: var(--space-10);
}

.ctaLink {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-10);
  width: fit-content;
  min-height: var(--layout-action-height);
  border-radius: var(--radius-control);
  margin-top: var(--space-24);
  margin-left: auto;
  justify-self: end;
  padding: clamp(var(--space-12), 1.3vw, var(--space-15)) var(--space-20);
  background: var(--color-primary);
  color: var(--color-cta-text-color);
  text-decoration: none;
  font-family: var(--font-body);
  font-size: var(--type-caption);
  font-weight: 700;
  letter-spacing: normal;
  text-transform: none;
  transition:
    filter var(--motion-fast) ease,
    transform var(--motion-fast) var(--motion-ease-out);

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      filter: brightness(1.08);
    }
  }

  &:active {
    transform: scale(0.98);
  }
}

.detailLayout[data-split="true"] {
  grid-template-columns: minmax(0, 9fr) minmax(0, 11fr);
}
@media (max-width: tablet) {
  .detailLayout[data-split="true"] {
    grid-template-columns: minmax(0, 1fr);
  }
}
.productPanel[data-solo="true"] {
  border-right: none;
}
.productPanel[data-overview="true"] {
  border-right: none;
}
@media (width > tablet) {
  .productPanel[data-solo="true"] {
    display: grid;
    grid-template-columns: minmax(0, 9fr) minmax(0, 11fr);
    column-gap: clamp(var(--space-24), 4vw, var(--space-64));
  }
  .productPanel[data-solo="true"] > :first-child {
    grid-column: 1/-1;
  }
}
.mainPhoto[data-zoomable="true"] {
  cursor: zoom-in;
}
.thumb[aria-pressed="true"] {
  border-color: var(--color-primary);
  box-shadow: var(--shadow-selection);
}
